import { ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Box, Chip, Collapse, Divider, IconButton } from '@mui/material';
import BackendService from 'BackendService';
import React, { useState } from 'react';
import { activityLogEventConfig, getPushNotificationEventLabel } from '../../models/ActivityLog';
import { Palette } from 'Palette';
import { StyledCard, StyledCardContent, StyledTypography } from '../styles/styles';
import ActivityLogPageEntryExtendedPanel from './ActivityLogPageEntryExtendedPanel';
import ActivityLogPageEntryLeftPanel from './ActivityLogPageEntryLeftPanel';
import { formatActivityLogTimestamp } from './activityLogEntryUtils';
import { useActivityLogPageEntryData } from './useActivityLogPageEntryData';

export interface ActivityLogPageEntryProps {
  log: any;
  showDetails?: boolean;
  tone?: keyof typeof Palette;
  allLogs?: any[];
  backendService?: BackendService;
}

const ActivityLogPageEntry: React.FC<ActivityLogPageEntryProps> = (props) => {
  if (!props.log) {
    console.warn('Invalid log entry in ActivityLogPageEntry:', props.log);
    return null;
  }

  return <ActivityLogPageEntryBody {...props} log={props.log} />;
};

const ActivityLogPageEntryBody: React.FC<
  ActivityLogPageEntryProps & { log: NonNullable<ActivityLogPageEntryProps['log']> }
> = ({
  log,
  showDetails = false,
  tone = 'default',
  allLogs = [],
  backendService: providedBackendService,
}) => {
  const [expanded, setExpanded] = useState(showDetails);

  const entryData = useActivityLogPageEntryData(log, expanded, allLogs, providedBackendService);

  const {
    backendService,
    contactNames,
    emailSentExtendedInfo,
    extendedInfoError,
    extendedInfoLoading,
    fetchedExtendedInfo,
    isContentDiffEvent,
    isEmailChangeEvent,
    isEmailEvent,
    isEmailOpenedOrClicked,
    isPushNotificationEvent,
    isUserProfileUpdated,
    loadingEmailSent,
    loadingEmailSentExtendedInfo,
    pushReceiptError,
    pushReceiptLoading,
    pushReceiptResult,
    setPushReceiptError,
    setPushReceiptLoading,
    setPushReceiptResult,
  } = entryData;

  let extendedInfo = fetchedExtendedInfo || log.extendedInfo;
  const pushNotificationSubtype = getPushNotificationEventLabel(
    log.eventType as import('../../models/ActivityLog').ActivityLogEventType,
    extendedInfo,
  );

  const emailExtendedInfo =
    isEmailOpenedOrClicked && emailSentExtendedInfo ? emailSentExtendedInfo : extendedInfo;
  const clickedLinkId = log.eventType === 'EMAIL_CLICKED' ? extendedInfo?.linkId : null;

  const eventConfig =
    activityLogEventConfig[log.eventType as keyof typeof activityLogEventConfig] ||
    activityLogEventConfig.DEFAULT;

  const ImpersonationBadge = () => {
    if (extendedInfoLoading) {
      return (
        <Chip
          size="small"
          label="Loading extended info..."
          color="info"
          variant="outlined"
          sx={{ ml: 1 }}
        />
      );
    }

    const isImpersonated =
      log.initiatedByDescription?.isImpersonated ||
      (log.onBehalfOfType && log.onBehalfOfExternalId);

    if (isImpersonated) {
      return (
        <Chip
          size="small"
          label="Impersonating"
          color="warning"
          variant="outlined"
          sx={{ ml: 1 }}
        />
      );
    }

    if (!extendedInfo?.impersonationContext) return null;
    return (
      <Chip
        size="small"
        label={`Impersonated by ${extendedInfo.impersonationContext.impersonatedBy}`}
        color="warning"
        variant="outlined"
        sx={{ ml: 1 }}
      />
    );
  };

  const handlePushReceiptRequest = async () => {
    const logId = log.externalId || log.id;
    if (!logId) return;
    setPushReceiptLoading(true);
    setPushReceiptError(null);
    setPushReceiptResult(null);
    try {
      const res = await backendService.getPushReceiptStatus(logId);
      setPushReceiptResult(res);
    } catch (err: any) {
      setPushReceiptError(err?.message || 'Failed to fetch receipt status');
    } finally {
      setPushReceiptLoading(false);
    }
  };

  return (
    <StyledCard tone={tone} sx={{ mb: 1 }}>
      <StyledCardContent sx={{ py: 2, px: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <StyledTypography tone={tone} fontSize="1.25rem" sx={{ mr: 1 }}>
              {eventConfig.icon}
            </StyledTypography>
            <Box sx={{ flex: 1 }}>
              <StyledTypography
                tone={tone}
                fontSize="0.875rem"
                fontWeight={600}
                sx={{ color: eventConfig.color }}
              >
                {eventConfig.label}
              </StyledTypography>
              {isPushNotificationEvent && pushNotificationSubtype && (
                <StyledTypography tone={tone} fontSize="0.75rem" sx={{ opacity: 0.85, mt: 0.25 }}>
                  {pushNotificationSubtype}
                </StyledTypography>
              )}
              <StyledTypography tone={tone} fontSize="0.75rem" sx={{ opacity: 0.7 }}>
                {formatActivityLogTimestamp(log.eventTimestamp)}
              </StyledTypography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ImpersonationBadge />
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
              sx={{ ml: 1 }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
            <ActivityLogPageEntryLeftPanel
              log={log}
              tone={tone}
              contactNames={contactNames}
              isEmailEvent={isEmailEvent}
            />
            <ActivityLogPageEntryExtendedPanel
              log={log}
              tone={tone}
              extendedInfoLoading={extendedInfoLoading}
              extendedInfoError={extendedInfoError}
              fetchedExtendedInfo={fetchedExtendedInfo}
              isContentDiffEvent={isContentDiffEvent}
              isEmailEvent={isEmailEvent}
              isEmailChangeEvent={isEmailChangeEvent}
              isUserProfileUpdated={isUserProfileUpdated}
              isEmailOpenedOrClicked={isEmailOpenedOrClicked}
              isPushNotificationEvent={isPushNotificationEvent}
              emailExtendedInfo={emailExtendedInfo}
              loadingEmailSent={loadingEmailSent}
              loadingEmailSentExtendedInfo={loadingEmailSentExtendedInfo}
              clickedLinkId={clickedLinkId}
              expanded={expanded}
              pushReceiptLoading={pushReceiptLoading}
              pushReceiptError={pushReceiptError}
              pushReceiptResult={pushReceiptResult}
              onPushReceiptRequest={handlePushReceiptRequest}
            />
          </Box>
        </Collapse>
      </StyledCardContent>
    </StyledCard>
  );
};

export default ActivityLogPageEntry;
